import React from 'react';
import { motion } from 'framer-motion';
import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';
import ImageWithFallback from '../components/ImageWithFallback';

const About = () => {
  const timeline = [
    {
      year: '2020',
      title: 'The Beginning',
      description: 'Travelogue was born from a simple idea: to share the world\'s most incredible destinations and travel stories.'
    },
    {
      year: '2021',
      title: 'Growing Community',
      description: 'Our community of travelers and storytellers began to grow, sharing experiences from around the globe.'
    },
    {
      year: '2022',
      title: 'Interactive Features',
      description: 'We launched interactive maps, destination guides, and a comprehensive storytelling platform.'
    },
    {
      year: '2023',
      title: 'Global Reach',
      description: 'Travelogue reached travelers in over 50 countries, becoming a trusted source for travel inspiration.'
    },
    {
      year: '2024',
      title: 'Future Vision',
      description: 'Continuing to innovate and inspire the next generation of travelers and storytellers.'
    }
  ];

  const team = [
    {
      name: 'Alex Chen',
      role: 'Founder & CEO',
      bio: 'Passionate about connecting people with the world\'s most beautiful destinations. Founded this platform to inspire authentic travel experiences.',
      image: 'https://i.pinimg.com/736x/e5/9b/20/e59b20abd160b497fc4ef70434ca1b4c.jpg'
    },
    {
      name: 'Sarah Mitchell',
      role: 'Head of Content',
      bio: 'Storyteller and travel writer who brings destinations to life through compelling narratives and authentic experiences.',
      image: 'https://i.pinimg.com/736x/d8/87/94/d887940e410603afac2f4a0b7e258b24.jpg'
    },
    {
      name: 'David Rodriguez',
      role: 'Community Manager',
      bio: 'Building and nurturing our global community of travelers, ensuring every member feels connected and inspired.',
      image: 'https://i.pinimg.com/1200x/29/e3/24/29e3244694dd0f4bf9f045b97b531308.jpg'
    }
  ];

  const stats = [
    { number: '500+', label: 'Destinations' },
    { number: '1000+', label: 'Stories' },
    { number: '50+', label: 'Countries' },
    { number: '10K+', label: 'Travelers' }
  ];

  return (
    <>
      <Helmet>
        <title>About Us - Travelogue</title>
        <meta name="description" content="Learn about Travelogue's mission to inspire and connect travelers worldwide through storytelling and destination guides." />
      </Helmet>

      <div className="min-h-screen bg-secondary-50 dark:bg-secondary-900">
        {/* Hero Section */}
        <div className="bg-white dark:bg-secondary-800 shadow-sm">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              className="text-center"
            >
              <h1 className="text-4xl md:text-5xl font-bold text-secondary-900 dark:text-white mb-6">
                About Travelogue
              </h1>
              <p className="text-xl text-secondary-600 dark:text-secondary-400 max-w-3xl mx-auto">
                We're passionate about connecting travelers with the world's most incredible destinations and inspiring stories.
              </p>
            </motion.div>
          </div>
        </div>

        {/* Stats */}
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="grid grid-cols-2 md:grid-cols-4 gap-8"
          >
            {stats.map((stat, index) => (
              <div key={index} className="text-center">
                <div className="text-3xl md:text-4xl font-bold text-primary-600 mb-2">
                  {stat.number}
                </div>
                <div className="text-secondary-600 dark:text-secondary-400">
                  {stat.label}
                </div>
              </div>
            ))}
          </motion.div>
        </div>

        {/* Mission */}
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.4 }}
            className="bg-white dark:bg-secondary-800 rounded-xl shadow-lg p-8 md:p-12"
          >
            <h2 className="text-3xl md:text-4xl font-bold text-secondary-900 dark:text-white mb-6 text-center">
              Our Mission
            </h2>
            <div className="prose prose-lg dark:prose-invert max-w-none text-center">
              <p className="text-secondary-700 dark:text-secondary-300 leading-relaxed">
                At Travelogue, we believe that every journey has a story worth sharing. Our mission is to inspire and connect travelers worldwide by providing a platform where incredible destinations meet compelling narratives. We curate the world's most beautiful places and authentic experiences, helping you discover your next adventure while preserving the magic of travel storytelling.
              </p>
            </div>
          </motion.div>
        </div>

        {/* Timeline */}
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.6 }}
            className="text-center mb-12"
          >
            <h2 className="text-3xl md:text-4xl font-bold text-secondary-900 dark:text-white mb-4">
              Our Journey
            </h2>
            <p className="text-lg text-secondary-600 dark:text-secondary-400">
              From humble beginnings to a global community of travelers
            </p>
          </motion.div>

          <div className="relative">
            <div className="absolute left-1/2 transform -translate-x-1/2 w-0.5 h-full bg-primary-200 dark:bg-primary-800"></div>
            {timeline.map((item, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, x: index % 2 === 0 ? -50 : 50 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.6, delay: 0.8 + index * 0.1 }}
                className={`flex items-center mb-8 ${index % 2 === 0 ? 'flex-row' : 'flex-row-reverse'}`}
              >
                <div className={`w-1/2 ${index % 2 === 0 ? 'pr-8 text-right' : 'pl-8 text-left'}`}>
                  <div className="bg-white dark:bg-secondary-800 rounded-lg shadow-lg p-6">
                    <div className="text-2xl font-bold text-primary-600 mb-2">{item.year}</div>
                    <h3 className="text-xl font-semibold text-secondary-900 dark:text-white mb-2">
                      {item.title}
                    </h3>
                    <p className="text-secondary-600 dark:text-secondary-400">
                      {item.description}
                    </p>
                  </div>
                </div>
                <div className="w-4 h-4 bg-primary-600 rounded-full border-4 border-white dark:border-secondary-800"></div>
                <div className="w-1/2"></div>
              </motion.div>
            ))}
          </div>
        </div>

        {/* Team */}
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.8 }}
            className="text-center mb-12"
          >
            <h2 className="text-3xl md:text-4xl font-bold text-secondary-900 dark:text-white mb-4">
              Meet Our Team
            </h2>
            <p className="text-lg text-secondary-600 dark:text-secondary-400">
              The passionate people behind Travelogue
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {team.map((member, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 1 + index * 0.1 }}
                className="bg-white dark:bg-secondary-800 rounded-xl shadow-lg p-6 text-center"
              >
                                 <ImageWithFallback
                   src={member.image}
                   alt={member.name}
                   className="w-32 h-32 rounded-full mx-auto mb-4 object-cover"
                   fallbackSrc={`https://via.placeholder.com/200x200/3B82F6/FFFFFF?text=${encodeURIComponent(member.name.charAt(0))}`}
                 />
                <h3 className="text-xl font-semibold text-secondary-900 dark:text-white mb-2">
                  {member.name}
                </h3>
                <p className="text-primary-600 font-medium mb-3">{member.role}</p>
                <p className="text-secondary-600 dark:text-secondary-400">
                  {member.bio}
                </p>
              </motion.div>
            ))}
          </div>
          
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 1.4 }}
            className="text-center mt-12"
          >
            <Link
              to="/team"
              className="inline-flex items-center px-6 py-3 bg-primary-600 text-white font-semibold rounded-lg hover:bg-primary-700 transition-colors duration-200"
            >
              Meet the Full Team
              <svg className="ml-2 w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
              </svg>
            </Link>
          </motion.div>
        </div>

        {/* Contact */}
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 1.2 }}
            className="bg-white dark:bg-secondary-800 rounded-xl shadow-lg p-8 md:p-12 text-center"
          >
            <h2 className="text-3xl md:text-4xl font-bold text-secondary-900 dark:text-white mb-6">
              Get in Touch
            </h2>
            <p className="text-lg text-secondary-600 dark:text-secondary-400 mb-8 max-w-2xl mx-auto">
              Have a story to share or want to collaborate? We'd love to hear from you!
            </p>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              <div>
                <h3 className="text-lg font-semibold text-secondary-900 dark:text-white mb-2">
                  Email
                </h3>
                <p className="text-secondary-600 dark:text-secondary-400">
                  hello@travelogue.com
                </p>
              </div>
              <div>
                <h3 className="text-lg font-semibold text-secondary-900 dark:text-white mb-2">
                  Social Media
                </h3>
                <p className="text-secondary-600 dark:text-secondary-400">
                  @travelogue
                </p>
              </div>
              <div>
                <h3 className="text-lg font-semibold text-secondary-900 dark:text-white mb-2">
                  Location
                </h3>
                <p className="text-secondary-600 dark:text-secondary-400">
                  Worldwide
                </p>
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </>
  );
};

export default About;
